// ./src/utils/api/DashboardService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Car } from "./CarService";

// ===========================================================================
// Fragments
// ===========================================================================

const STATS_UNITS_FRAGMENT = gql`
  fragment StatsUnitsFields on StatsUnits {
    distanceUnit
    volumeUnit
    consumptionUnit
    firstOdometer
    lastOdometer
    travelsDistance
    firstRefuelOdometer
    consumptionDistance
    refuelsVolume
    firstRefuelVolume
    consumptionVolume
    consumption
  }
`;

const CAR_STATS_FRAGMENT = gql`
  ${STATS_UNITS_FRAGMENT}
  fragment CarStatsFields on CarStats {
    carId
    year
    month
    refuelsCount
    expensesCount
    revenuesCount
    maintenanceCount
    checkpointsCount
    travelsCount
    latestRefuelId
    latestExpenseId
    latestTravelId
    latestRevenueId
    firstRefuelId
    firstRecordAt
    lastRecordAt
    inCarUnits {
      ...StatsUnitsFields
    }
    inUserUnits {
      ...StatsUnitsFields
    }
  }
`;

const STATS_MONETARY_FRAGMENT = gql`
  fragment StatsMonetaryFields on StatsMonetary {
    carId
    year
    month
    currency
    refuelsCost
    refuelsTaxes
    expensesCost
    expensesFees
    expensesTaxes
    maintenanceCost
    revenuesAmount
  }
`;

const DASHBOARD_AVERAGES_FRAGMENT = gql`
  fragment DashboardAveragesFields on DashboardAverages {
    currency
    monthsCount
    monthlyExpense
    monthlyRevenue
    monthlyRefuelsVolume
    monthlyMileageInCarUnits
    monthlyMileageInUserUnits
    monthlyConsumption
  }
`;

const COST_OF_OWNERSHIP_FRAGMENT = gql`
  fragment CostOfOwnershipFields on CostOfOwnership {
    totalCost
    perMonth
    perDistance
    monthsOwned
    ownershipStartAt
  }
`;

// ===========================================================================
// Queries
// ===========================================================================

const DASHBOARD_GET_QUERY = gql`
  ${CAR_STATS_FRAGMENT}
  ${STATS_MONETARY_FRAGMENT}
  ${DASHBOARD_AVERAGES_FRAGMENT}
  ${COST_OF_OWNERSHIP_FRAGMENT}
  query DashboardGet($params: DashboardParamsInput) {
    dashboardGet(params: $params) {
      code
      data {
        carId
        car {
          id
          label
          model
          color
          manufacturedIn
          mileageIn
          initialMileage
          bodyTypeId
          transmissionTypeId
          engineTypeId
          makeId
          firstRecordId
          ownerNumber
          entityAttachmentId
          uploadedFileId
          driveType
          licensePlate
          mainTankFuelType
          mainTankVolume
          mainTankVolumeEnteredIn
          mainTankDefaultGrade
          addlTankFuelType
          addlTankVolume
          addlTankVolumeEnteredIn
          addlTankDefaultGrade
          carBodyType {
            id
            name
          }
          carTransmissionType {
            id
            code
            name
          }
          carEngineType {
            id
            code
            name
          }
          vehicleMake {
            id
            makeName
          }
          uploadedFile {
            id
            fileUrl
            thumbnailUrl
          }
        }
        totalStats {
          ...CarStatsFields
        }
        totalMonetary {
          ...StatsMonetaryFields
        }
        currentMonthStats {
          ...CarStatsFields
        }
        currentMonthMonetary {
          ...StatsMonetaryFields
        }
        previousMonthStats {
          ...CarStatsFields
        }
        previousMonthMonetary {
          ...StatsMonetaryFields
        }
        averages {
          ...DashboardAveragesFields
        }
        daysRemainingInMonth
        recentTravelPurposes {
          purpose
        }
        costOfOwnership {
          ...CostOfOwnershipFields
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FLEET_SUMMARY_GET_QUERY = gql`
  query DashboardFleetSummaryGet($params: FleetSummaryParamsInput) {
    dashboardFleetSummaryGet(params: $params) {
      code
      data {
        year
        month
        currency
        distanceUnit
        volumeUnit
        refuelsCost
        maintenanceCost
        otherExpensesCost
        totalCost
        revenuesAmount
        refuelsCount
        maintenanceCount
        otherExpensesCount
        revenuesCount
        refuelsVolume
        totalDistance
        fuelCostPerDistance
        runningCostPerDistance
        minYear
        minMonth
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// ===========================================================================
// Types
// ===========================================================================

/**
 * Distance, volume, and consumption values converted to specific units.
 * Used for both lifetime totals and monthly stats.
 */
export interface StatsUnits {
  /** Distance unit: km, mi */
  distanceUnit: string;
  /** Volume unit: l, gal-us, gal-uk */
  volumeUnit: string;
  /** Consumption unit: l100km, km-l, mpg-us, mpg-uk, mi-l */
  consumptionUnit: string;
  /** First odometer reading in period */
  firstOdometer?: number | undefined;
  /** Last odometer reading in period */
  lastOdometer?: number | undefined;
  /** Total distance from completed travels */
  travelsDistance?: number | undefined;
  /** First refuel odometer reading */
  firstRefuelOdometer?: number | undefined;
  /** Distance used for consumption calculation */
  consumptionDistance?: number | undefined;
  /** Total fuel volume purchased */
  refuelsVolume?: number | undefined;
  /** First refuel volume */
  firstRefuelVolume?: number | undefined;
  /** Fuel volume for consumption calculation */
  consumptionVolume?: number | undefined;
  /** Calculated fuel consumption */
  consumption?: number | undefined;
}

/**
 * Currency-independent stats for a single car.
 * Used for both lifetime totals (year=0, month=0) and monthly breakdowns.
 */
export interface CarStats {
  carId: string;
  /** Year of the stats period (0 for lifetime totals) */
  year: number;
  /** Month of the stats period (0 for lifetime totals) */
  month: number;
  refuelsCount?: number | undefined;
  expensesCount?: number | undefined;
  revenuesCount?: number | undefined;
  maintenanceCount?: number | undefined;
  checkpointsCount?: number | undefined;
  travelsCount?: number | undefined;
  latestRefuelId?: string | undefined;
  latestExpenseId?: string | undefined;
  latestTravelId?: string | undefined;
  latestRevenueId?: string | undefined;
  firstRefuelId?: string | undefined;
  firstRecordAt?: string | undefined;
  lastRecordAt?: string | undefined;
  /** Values converted to car's preferred units */
  inCarUnits?: StatsUnits | undefined;
  /** Values converted to user's preferred units */
  inUserUnits?: StatsUnits | undefined;
}

/**
 * Monetary totals for a single car in a specific currency.
 * Used for both lifetime totals (year=0, month=0) and monthly breakdowns.
 */
export interface StatsMonetary {
  carId: string;
  /** Year of the stats period (0 for lifetime totals) */
  year: number;
  /** Month of the stats period (0 for lifetime totals) */
  month: number;
  currency: string;
  refuelsCost?: number | undefined;
  refuelsTaxes?: number | undefined;
  expensesCost?: number | undefined;
  expensesFees?: number | undefined;
  expensesTaxes?: number | undefined;
  maintenanceCost?: number | undefined;
  revenuesAmount?: number | undefined;
}

/**
 * Rolling averages computed from the last N months of data.
 * Monetary values are in the user's home currency.
 */
export interface DashboardAverages {
  /** The currency used for monetary averages (user's home currency) */
  currency: string;
  /** Number of months used to compute the averages */
  monthsCount?: number | undefined;
  /** Average monthly expense amount (in home currency) */
  monthlyExpense?: number | undefined;
  /** Average monthly revenue amount (in home currency) */
  monthlyRevenue?: number | undefined;
  /** Average monthly refuels volume in user's preferred volume unit */
  monthlyRefuelsVolume?: number | undefined;
  /** Average monthly mileage in car's preferred distance unit */
  monthlyMileageInCarUnits?: number | undefined;
  /** Average monthly mileage in user's preferred distance unit */
  monthlyMileageInUserUnits?: number | undefined;
  /** Average fuel consumption in user's preferred consumption unit */
  monthlyConsumption?: number | undefined;
}

/**
 * A travel purpose entry.
 */
export interface TravelPurpose {
  /** The purpose text (most recent spelling for normalized duplicates) */
  purpose: string;
}

/**
 * True cost of ownership summary for a single car.
 * Monetary values are in the user's home currency.
 * perDistance is in the user's preferred distance unit (distanceIn from profile).
 */
export interface CostOfOwnership {
  /** Total cost: all refuels + all expenses in home currency */
  totalCost?: number | undefined;
  /** Average cost per calendar month since ownership start */
  perMonth?: number | undefined;
  /** Cost per distance unit driven (user's distanceIn preference) */
  perDistance?: number | undefined;
  /** Full calendar months from ownershipStartAt to now (minimum 1) */
  monthsOwned: number;
  /** The date used as ownership baseline (ISO UTC) */
  ownershipStartAt?: string | undefined;
}

/**
 * Dashboard data for a single car, combining lifetime stats,
 * current and previous month summaries, and rolling averages.
 */
export interface DashboardItem {
  carId: string;
  /** Full car object (null when includeCars is false) */
  car?: Car | undefined;
  /** Lifetime currency-independent stats */
  totalStats: CarStats;
  /** Lifetime monetary totals per currency */
  totalMonetary: StatsMonetary[];
  /** Current month currency-independent stats */
  currentMonthStats: CarStats;
  /** Current month monetary totals per currency */
  currentMonthMonetary: StatsMonetary[];
  /** Previous month currency-independent stats */
  previousMonthStats: CarStats;
  /** Previous month monetary totals per currency */
  previousMonthMonetary: StatsMonetary[];
  /** Rolling averages for the last N months */
  averages: DashboardAverages;
  /** Number of days remaining in the current month */
  daysRemainingInMonth: number;
  /** Recent unique travel purposes across accessible cars */
  recentTravelPurposes?: TravelPurpose[] | undefined;
  /** True cost of ownership summary */
  costOfOwnership?: CostOfOwnership | undefined;
}

export interface DashboardParamsInput {
  /** Car IDs to include. Empty or omitted = all accessible cars */
  carIds?: string[] | undefined;
  /** User's timezone offset in minutes from UTC (like JS getTimezoneOffset()) */
  timezoneOffset?: number | undefined;
  /** Number of months to use for rolling averages (default: 3) */
  avgMonths?: number | undefined;
  /** Whether to include full car objects in the response (default: true) */
  includeCars?: boolean | undefined;
  /** Whether to include expense/revenue breakdowns by kind (default: false) */
  includeBreakdowns?: boolean | undefined;
  /** Max number of recent unique travel purposes to return (default: 10) */
  recentTravelPurposesCount?: number | undefined;
}

/**
 * Fleet-wide monthly summary aggregated across all active vehicles.
 * All monetary values are in the user's home currency.
 * All physical values are in the user's preferred units.
 */
export interface FleetSummary {
  /** Requested year */
  year: number;
  /** Requested month (1-12) */
  month: number;
  /** User's home currency used for all monetary values */
  currency: string;
  /** User's preferred distance unit */
  distanceUnit: string;
  /** User's preferred volume unit */
  volumeUnit: string;
  /** Total refuel cost across all vehicles (home currency) */
  refuelsCost: number;
  /** Total maintenance cost across all vehicles */
  maintenanceCost: number;
  /** Total other expenses cost across all vehicles */
  otherExpensesCost: number;
  /** Total cost: refuels + maintenance + other */
  totalCost: number;
  /** Total revenue across all vehicles */
  revenuesAmount: number;
  /** Total number of refuel records */
  refuelsCount: number;
  /** Total number of maintenance records */
  maintenanceCount: number;
  /** Total number of other expense records */
  otherExpensesCount: number;
  /** Total number of revenue records */
  revenuesCount: number;
  /** Total fuel volume purchased (user's volume unit) */
  refuelsVolume: number;
  /** Total distance driven across all vehicles (user's distance unit) */
  totalDistance: number;
  /** Fuel cost per distance unit (null if no distance) */
  fuelCostPerDistance: number | null;
  /** Running cost per distance unit (null if no distance) */
  runningCostPerDistance: number | null;
  /** Earliest year with data (for back-navigation bound) */
  minYear: number;
  /** Earliest month with data in minYear (for back-navigation bound) */
  minMonth: number;
}

export interface FleetSummaryParamsInput {
  /** Year to fetch summary for (defaults to current year based on timezone) */
  year?: number | undefined;
  /** Month to fetch summary for (1-12, defaults to current month based on timezone) */
  month?: number | undefined;
  /** User's timezone offset in minutes from UTC (like JS getTimezoneOffset()) */
  timezoneOffset?: number | undefined;
}

// ===========================================================================
// Service
// ===========================================================================

/**
 * Dashboard service that handles the unified dashboard API calls.
 * - get(): Returns per-vehicle lifetime stats, current/previous month summaries,
 *   rolling averages, and cost of ownership for all accessible cars.
 * - getFleetSummary(): Returns fleet-wide aggregated stats for a specific month.
 */
export class DashboardService {
  /**
   * Get dashboard data for all accessible cars (or specific cars if carIds provided)
   */
  static async get(params?: DashboardParamsInput): Promise<OpResult> {
    const result = await ApiService.executeQuery(DASHBOARD_GET_QUERY, {
      params: params || {},
    });
    return result;
  }

  /**
   * Get fleet-wide monthly summary aggregated across all active vehicles.
   */
  static async getFleetSummary(params?: FleetSummaryParamsInput): Promise<OpResult> {
    const result = await ApiService.executeQuery(FLEET_SUMMARY_GET_QUERY, {
      params: params || {},
    });
    return result;
  }
}